import React, { useEffect, useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getTargetList, getUserBroker } from 'config/api';
import { useModal } from 'hooks/useModal';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useTargetAchievementSupportContext } from 'contexts/TargetAchievementSupportContext';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import Loading from 'components/commons/loading/Loading';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import {
  Account,
  AccountId,
  Target,
  TargetId,
  TargetModalStatus,
  TargetType,
} from '../utils/type';
import { useBirthday } from '../utils/birthday';
import { dateToString } from '../utils/utils';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import { useTargetSupportRouting } from '../hooks/useTargetSupportRouting';
import { TargetBasicInfoForm } from '../components/features/share/TargetBasicInfoForm';
import { SetAccount } from '../components/features/update/step1/SetAccount';
import { SetExcludeAmount } from '../components/features/update/step1/SetExcludeAmount';
import { Page, StepArea } from '../components/features/update/StepArea';
import { ExitSetHowToSaveModal } from '../components/features/update/modal/ExitSetHowToSaveModal';

export type Mode = 'new' | 'edit';

export type validateProps = {
  isValid: boolean;
  error?: string;
};

const TargetUpdateStep1Internal = ({
  id,
  mode,
  target,
  updateTarget,
  targets,
  accounts,
  isConfirm,
}: {
  id: TargetId;
  mode: Mode;
  target: Target;
  updateTarget: (data: Target | null) => void;
  targets: Target[];
  accounts: Account[];
  isConfirm: boolean;
}) => {
  const {
    goTargetReplaceDetail,
    goTargetPushUpdateStep2,
    goTargetReplaceUpdateConfirm,
  } = useTargetSupportRouting();

  const initialSelectedAccounts = target.accountList.map(list => list.account);
  const [selectedAccounts, setSelectedAccounts] = useState<AccountId[]>(
    initialSelectedAccounts
  );

  const [excludeAmount, setExcludeAmount] = useState<number>(
    target.excludeAmount ?? 0
  );

  const initialValidate = { isValid: false };
  const [validateTargetAmount, setValidateTargetAmount] = useState<
    validateProps
  >(initialValidate);

  const [validateExcludeAmount, setValidateExcludeAmount] = useState<
    validateProps
  >(initialValidate);

  const [
    isShowExitModal,
    { hideModal: hideExitModal, showModal: showExitModal },
  ] = useModal();

  const { setBack } = useCustomizeHeaderContext();
  useEffect(() => {
    if (!isConfirm) {
      setBack({ backFunction: showExitModal });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { setIsShowMenu } = useGlobalMenuContext();
  useEffect(() => {
    return setIsShowMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const totalAssetAmount = accounts
      .filter(
        account =>
          selectedAccounts.includes(account.account) && account.available
      )
      .reduce((acc, v) => acc + (v.totalAssetAmount ?? 0), 0);

    if (totalAssetAmount - excludeAmount > target.targetAmount) {
      setValidateTargetAmount({
        isValid: false,
        error: '設定した口座の合計額が目標額を超えています',
      });
    } else {
      setValidateTargetAmount({ isValid: true });
    }

    const initialAmount = accounts
      .filter(
        account =>
          selectedAccounts.includes(account.account) && account.available
      )
      .reduce((acc, v) => acc + (v.totalAssetAmount ?? 0), 0);

    if (excludeAmount < 0) {
      setValidateExcludeAmount({
        isValid: false,
        error: '除外金額には0以上の数を入力してください',
      });
    } else if (excludeAmount > initialAmount) {
      setValidateExcludeAmount({
        isValid: false,
        error: '初期投資金額を上回っています',
      });
    } else {
      setValidateExcludeAmount({ isValid: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccounts, excludeAmount]);

  const isFormValid =
    validateExcludeAmount.isValid && validateTargetAmount.isValid;

  const goNext = (): void => {
    const next = () => {
      isConfirm
        ? goTargetReplaceUpdateConfirm(id, mode)
        : goTargetPushUpdateStep2(id, mode);
    };

    const updateAccounts = accounts.filter(account =>
      selectedAccounts.includes(account.account)
    );

    const updateAssetAmount = updateAccounts
      .map(account => (account.available ? account.totalAssetAmount : 0))
      .reduce((acc, v) => acc + v, 0);

    const updateTarget_ = {
      ...target,
      accountList: updateAccounts.map(account => {
        return {
          account: account.account,
          brokerName: account.brokerName,
          available: account.available,
          totalAssetAmount: account.totalAssetAmount,
        };
      }),
      assetAmount: updateAssetAmount,
      excludeAmount,
    };

    updateTarget(updateTarget_);
    isFormValid && next();
  };

  const goBack = () =>
    isConfirm ? goTargetReplaceUpdateConfirm(id, mode) : showExitModal();

  return (
    <>
      <div className={`${mode === 'new' ? 'mt-6' : 'mt-8'}`}>
        {mode === 'new' && <StepArea {...{ currentPage: 'step1' as Page }} />}
        <TargetBasicInfoForm {...{ target: target }} />
        <SetAccount
          {...{
            id,
            targets,
            accounts,
            selectedAccounts,
            setSelectedAccounts,
            validateTargetAmount,
          }}
        />
        <SetExcludeAmount
          {...{
            excludeAmount,
            setExcludeAmount,
            validateExcludeAmount,
          }}
        />
        <div className="flex flex-col mt-6 mb-16 mx-2">
          <button
            className={`flex justify-center items-center mb-4 py-2 text-base font-bold text-white bg-tradewell rounded-lg  ${
              isFormValid ? '' : 'opacity-20 cursor-not-allowed'
            }`}
            onClick={goNext}
            disabled={!isFormValid}>
            {isConfirm ? '変更する' : '次へ'}
          </button>
          <button
            className="flex justify-center items-center py-2 text-base font-bold text-bluegray-700 bg-gray-50 border rounded-lg border-bluegray-400 shadow"
            onClick={goBack}>
            戻る
          </button>
        </div>
      </div>

      <ExitSetHowToSaveModal
        {...{
          hide: hideExitModal,
          isShow: isShowExitModal,
          onQuit: () => {
            updateTarget(null);
            goTargetReplaceDetail(id);
          },
          onContinueInput: hideExitModal,
        }}
      />
    </>
  );
};

const TargetUpdateStep1 = (): React.ReactElement => {
  useCustomHeaderTitle('貯める口座の選択');
  const { id, mode } = useParams<{ id: string; mode: Mode }>();

  const {
    globalTarget,
    updateGlobalTarget,
    confirmGlobalTarget,
    updateConfirmGlobalTarget,
    isFirstFlg,
    updateIsFirstFlg,
  } = useTargetAchievementSupportContext();

  const { data: targets_ } = useQuery(
    'TargetUpdateStep1tTargetList',
    () => getTargetListAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: accounts_ } = useQuery(
    'TargetUpdateStep1UserBroker',
    () => getUserBrokerAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { birthday } = useBirthday();

  if (
    targets_ === null ||
    targets_ === undefined ||
    accounts_ === null ||
    accounts_ === undefined
  )
    return <Loading loading style={{ marginTop: '3rem' }} />;

  const targets = targets_ as Target[];
  const accounts = accounts_ as Account[];
  const selectedTarget = targets.find((t: Target) => t.id === id);

  if (!selectedTarget) {
    return <Loading loading style={{ marginTop: '3rem' }} />;
  }

  if (!isFirstFlg && globalTarget === null && confirmGlobalTarget === null) {
    return <Redirect to={`/target/${id}`} />;
  } else {
    updateIsFirstFlg(false);
  }

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <TargetUpdateStep1GetGlobalTarget
        {...{
          id,
          mode,
          targets,
          accounts,
          selectedTarget,
          globalTarget,
          updateGlobalTarget,
          confirmGlobalTarget,
          updateConfirmGlobalTarget,
        }}
      />
    </PremiumCheck>
  );
};

const TargetUpdateStep1GetGlobalTarget = ({
  id,
  mode,
  targets,
  accounts,
  selectedTarget,
  globalTarget,
  updateGlobalTarget,
  confirmGlobalTarget,
  updateConfirmGlobalTarget,
}: {
  id: TargetId;
  mode: Mode;
  targets: Target[];
  accounts: Account[];
  selectedTarget: Target;
  globalTarget: Target | null;
  updateGlobalTarget: (data: Target | null) => void;
  confirmGlobalTarget: Target | null;
  updateConfirmGlobalTarget: (data: Target | null) => void;
}) => {
  useEffect(() => {
    if (selectedTarget !== undefined && globalTarget === null) {
      const updateTarget = {
        ...selectedTarget,
        accountList: [],
        yield: 0,
        startDate: dateToString(new Date(selectedTarget.startDate)),
        endDate: dateToString(new Date(selectedTarget.endDate)),
      };
      updateGlobalTarget(updateTarget);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (globalTarget === null || id !== globalTarget.id)
    return <Loading loading style={{ marginTop: '3rem' }} />;

  const isConfirm = confirmGlobalTarget !== null ? true : false;

  return (
    <TargetUpdateStep1Internal
      {...{
        id,
        mode,
        target:
          isConfirm && confirmGlobalTarget !== null
            ? confirmGlobalTarget
            : globalTarget,
        updateTarget: isConfirm
          ? updateConfirmGlobalTarget
          : updateGlobalTarget,
        targets,
        accounts,
        isConfirm,
      }}
    />
  );
};

const getUserBrokerAPI = async () => {
  const ret = await getUserBroker();
  if (ret === null || ret.Items === null) return null;
  return ret.Items.reduce((acc, v) => {
    return [
      ...acc,
      (item => {
        return {
          totalAssetAmount: item.TotalAssetAmount,
          account: item.Account,
          available: item.Available,
          brokerName: item.BrokerName,
          aggregateSource: item.AggregateSource,
          brokerType: item.BrokerType,
        };
      })(v),
    ];
  }, [] as Account[]);
};

const getTargetListAPI = async () => {
  const res_ = await getTargetList();

  if (res_.Items === null) return null;
  const res = res_.Items.map(item => {
    return {
      id: item.ID,
      title: item.Title,
      refType: item.RefType as TargetType,
      startDate: item.StartDate,
      endDate: item.EndDate,
      targetAmount: item.TargetAmount,
      accountList: item.AccountList.map(list => {
        return {
          account: list.Account,
          brokerName: list.BrokerName,
          available: list.Available,
          totalAssetAmount: list.TotalAssetAmount,
        };
      }),
      excludeAmount: item.ExcludeAmount,
      reserveAmount: item.ReserveAmount,
      assetAmount: item.AssetAmount,
      yield: item.Yield,
      achieved: item.Achieved,
      status: item.Status as TargetModalStatus,
    };
  });

  return res as Target[];
};

export default TargetUpdateStep1;
